import { styled } from 'styled-components';
import colors from '../../../constants/colors';
import Button, { buttonType } from '../../general/Button';
import { ReactSVG } from 'react-svg';
import { useDropzone, FileRejection } from 'react-dropzone';
import { useEffect, useState } from 'react';
import { importPhotos } from '../../../api/photos';
import { useTranslation } from 'react-i18next';
import styleConstants from '../../../constants/styleConstants';
import Loader from '../../general/Loader';
import { LogIn } from '../../../api/auth';
import { LoginState, useAuth } from '../../../hook/useAuth';
import { createPortal } from 'react-dom';
import Hotjar from '@hotjar/browser';

interface ModalProps {
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  folderID?: string;
  addPhotosToImport: (newPhotos: File[]) => void;
}

type FlexDirection =
  | 'column'
  | 'inherit'
  | '-moz-initial'
  | 'initial'
  | 'revert'
  | 'unset'
  | 'column-reverse'
  | 'row'
  | 'row-reverse'
  | undefined;

const baseStyle = {
  width: '100%',
  height: '300px',
  display: 'flex',
  flexDirection: 'column' as FlexDirection,
  justifyContent: 'center',
  alignItems: 'center',
  padding: '20px',
  borderWidth: 2,
  borderRadius: 12,
  borderColor: colors.gray400,
  borderStyle: 'dashed',
  outline: 'none',
  transition: 'border .24s ease-in-out',
  fontFamily: 'DM Sans',
  fontSize: '14px',
  fontWeight: 400,
  cursor: 'pointer',
};

const ImportPhotoModal: React.FC<ModalProps> = ({
  visible,
  setVisible,
  folderID,
  addPhotosToImport,
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { isReady, userInfo, asklogin } = useAuth();

  const onDrop = (newFiles: File[]) => {
    setFiles([...files, ...newFiles]);
  };

  const onDropAccepted = (acceptedFiles: File[]) => {
    setFiles([...files, ...acceptedFiles]);
  };

  const onDropRejected = (rejectedFiles: FileRejection[]) => {
    console.log('Fichiers rejetés:', rejectedFiles);
  };

  // Détecter Chrome Android spécifiquement
  const isChromeAndroid = /Chrome/.test(navigator.userAgent) && /Android/.test(navigator.userAgent);
  
  // Aussi détecter les appareils mobiles en général pour debug
  const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 'ontouchstart' in window;
  


    // Handler pour la sélection de fichiers mobile
  const handleDirectFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const fileArray = Array.from(e.target.files);
      setFiles(prev => [...prev, ...fileArray]);
    }
  };
  
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropAccepted,
    onDropRejected,
    accept: isChromeAndroid ? {
      // Pour Chrome Android, être plus permissif
      '*': ['.png', '.jpeg', '.jpg', '.webp', '.heic']
    } : {
      'image/*': ['.png', '.jpeg', '.jpg', '.webp', '.heic'],
    },
    maxSize: 26214400,
    multiple: true,
    // Sur Chrome Android, forcer l'utilisation du click
    noClick: false,
    noKeyboard: false,
    // Désactiver le drag&drop sur Chrome Android (problématique)
    noDrag: isChromeAndroid,
  });

  const { t } = useTranslation(['common']);

  const importPhoto = async () => {
    try {
      Hotjar.event('Import de photos');
      addPhotosToImport([...files]);
      setVisible(false);
    } catch (error) {
      console.error('Erreur import:', error);
    }
  };

  useEffect(() => {
    setFiles([]);
    setIsLoading(false);
  }, [visible]);

  return createPortal(
    <Container $show={visible} id="importModal">
      <ComponentContainer>
        <FormTopContainer>
          <div style={{ width: '140px', height: '1px' }}></div>
          <Title>{t('photos.selectPhotos')}</Title>
          <Button
            $type={buttonType['white-noborder']}
            onClick={() => setVisible(false)}
          >
            <ReactSVG src="/svg/cross.svg" />
            {t('general.close')}
          </Button>
        </FormTopContainer>
        <FormContainer>
          {/* Pour mobile (Chrome Android ou autres), afficher directement le bouton qui marche */}
          {isMobile ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleDirectFileInput}
                style={{ display: 'none' }}
                id="mobile-file-input"
              />
              <Button
                $type={buttonType.white}
                onClick={() => document.getElementById('mobile-file-input')?.click()}
                style={{ 
                  padding: '16px 32px',
                  fontSize: '16px',
                  marginBottom: '16px',
                  border: `2px dashed ${colors.gray400}`,
                  borderRadius: '12px'
                }}
              >
                📷 {t('photos.selectPhotosButton')}
              </Button>
              <p style={{ color: colors.gray600, fontSize: '14px' }}>
                {t('photos.mobileOptimized')}
              </p>
            </div>
          ) : (
            /* Interface normale pour les autres navigateurs */
            <div {...getRootProps({ style: baseStyle })}>
              <input 
                {...getInputProps({ 
                  multiple: true,
                  accept: "image/*"
                })} 
              />
              <ReactSVG src="/svg/upload_photo.svg" />
              <p>
                <b>{t('photos.clickHere')}</b> {t('photos.dragPhotos')}
              </p>
              {isDragActive && (
                <p style={{ color: colors.green, fontWeight: 'bold' }}>
                  {t('photos.dropFilesHere')}
                </p>
              )}
            </div>
          )}
          
          {/* Affichage des fichiers sélectionnés */}
          {files.length > 0 && (
            <div style={{
              backgroundColor: colors.green,
              color: colors.white,
              padding: '12px 16px',
              borderRadius: '8px',
              marginTop: '16px',
              fontSize: '16px',
              fontWeight: 700,
              textAlign: 'center'
            }}>
              ✅ {t('photos.photosSelected', { count: files.length })}
            </div>
          )}
          
          <Button
            style={{ marginTop: '8px' }}
            $type={buttonType.black}
            onClick={importPhoto}
          >
            {isLoading ? <Loader /> : t('photos.validate')}
          </Button>
          

        </FormContainer>
      </ComponentContainer>
    </Container>,
    document.body,
  );
};

const Container = styled.div<{ $show: boolean }>`
  width: 100%;
  height: 100%;

  display: ${({ $show }) => ($show ? 'flex' : 'none')};
  flex-direction: column;
  justify-content: center;
  align-items: center;

  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;

  overflow: visible;

  background-color: rgba(52, 64, 84, 0.7);
`;

const ComponentContainer = styled.div`
  width: 40%;

  @media (max-width: ${styleConstants.breakpointLg}) {
    width: 320px;
  }
`;

const FormTopContainer = styled.div`
  height: 84px;

  display: flex;
  flex-direction: row;
  justify-content: space-around;
  align-items: center;

  position: relative;

  background-color: ${colors.white};
  color: ${colors.black};

  border-radius: 8px 8px 0 0;
`;

const Title = styled.div`
  font-family: 'Playfair Display';
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  text-align: center;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  background-color: ${colors.gray100};
  color: ${colors.black};

  box-sizing: border-box;
  border-radius: 0 0 8px 8px;
  padding: 24px;
`;

export default ImportPhotoModal;
